import { apiRequest } from "./api";
import { createPkce } from "./pkce";
import type { RuntimeConfig } from "./types";

const SIGNED_OUT_KEY = "support:customer-signed-out";
const REDIRECT_KEY = "support:customer-sso-redirect";

interface RedirectIntent {
  state: string;
  verifier: string;
  redirectUri: string;
  createdAt: number;
  returnTo: string;
}

export function saveCustomerSsoRedirect(intent: Omit<RedirectIntent, "createdAt" | "returnTo">, returnTo: string) {
  // Fail visibly instead of navigating away if PKCE cannot survive the redirect.
  sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ ...intent, createdAt: Date.now(), returnTo }));
}

export async function finishCustomerSsoRedirect(callbackUrl: string) {
  const url = new URL(callbackUrl);
  const stored = sessionStorage.getItem(REDIRECT_KEY);
  sessionStorage.removeItem(REDIRECT_KEY); // Consume once, even if validation fails.
  if (!stored) throw new Error("This sign-in request has expired. Please sign in again.");
  const intent = JSON.parse(stored) as RedirectIntent;
  const code = url.searchParams.get("code");
  if (!code || url.searchParams.get("state") !== intent.state
    || intent.redirectUri !== url.origin + "/auth/sso/callback"
    || typeof intent.createdAt !== "number" || Date.now() - intent.createdAt > 600_000
    || intent.createdAt > Date.now() || !intent.verifier) {
    throw new Error("This sign-in request is invalid or expired. Please sign in again.");
  }
  const session = await apiRequest<unknown>("/auth/customer/exchange", {
    method: "POST", signal: AbortSignal.timeout(15_000),
    body: { code, authorizationCode: code, codeVerifier: intent.verifier, redirectUri: intent.redirectUri, state: intent.state },
  });
  const returnUrl = new URL(intent.returnTo, url.origin);
  const returnTo = returnUrl.origin === url.origin && !returnUrl.pathname.startsWith("//")
    && !/^\/(?:auth|agent)(?:\/|$)/.test(returnUrl.pathname)
    ? returnUrl.pathname + returnUrl.search + returnUrl.hash : "/";
  return { session, returnTo };
}

export function customerSignedOut(): boolean {
  try { return sessionStorage.getItem(SIGNED_OUT_KEY) === "1"; } catch { return false; }
}

export function rememberCustomerSignOut(signedOut: boolean) {
  try {
    if (signedOut) sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    else sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch { /* In-memory session state still works when storage is unavailable. */ }
}

// Successful Dash authorization has no loginUrl. Always request fresh PKCE authorization.
export async function requestCustomerSso(runtime: RuntimeConfig, origin: string) {
  const { authorizeUrl, clientId } = runtime.dashSso ?? {};
  if (!authorizeUrl || !clientId) throw new Error("Dash sign-in is not configured. Please contact support.");
  const pkce = await createPkce();
  const redirectUri = `${origin}/auth/sso/callback`;
  const url = new URL(authorizeUrl, origin);
  Object.entries({
    client_id: clientId, redirect_uri: redirectUri, state: pkce.state,
    code_challenge: pkce.challenge, code_challenge_method: "S256", response_mode: "json",
  }).forEach(([key, value]) => url.searchParams.set(key, value));
  const response = await fetch(url.toString(), {
    credentials: "include", headers: { Accept: "application/json" },
    cache: "no-store", signal: AbortSignal.timeout(15_000),
  });
  const authorization = await response.json().catch(() => ({})) as {
    authorizationCode?: string; code?: string; state?: string; loginUrl?: string;
  };
  if (response.status === 401) {
    const loginUrl = new URL(authorization.loginUrl || runtime.dashSso?.loginUrl || "/login", url.origin);
    if (loginUrl.origin !== url.origin || loginUrl.username || loginUrl.password) {
      throw new Error("Dash returned an invalid sign-in URL.");
    }
    // Dash only accepts same-site post-login paths. Its query-mode authorize
    // endpoint preserves this intent through password/2FA and returns code+state.
    url.searchParams.set("response_mode", "query");
    return {
      kind: "login-required" as const, authorizeUrl: url.toString(),
      intent: { state: pkce.state, verifier: pkce.verifier, redirectUri },
    };
  }
  if (!response.ok) throw new Error("Dash sign-in could not be checked. Please try again.");
  if (authorization.state !== pkce.state) throw new Error("The SSO response state did not match.");
  const code = authorization.authorizationCode ?? authorization.code;
  if (!code) throw new Error("Dash did not return an authorization code.");
  const session = await apiRequest<unknown>("/auth/customer/exchange", {
    method: "POST", signal: AbortSignal.timeout(15_000),
    body: { code, authorizationCode: code, codeVerifier: pkce.verifier, redirectUri, state: pkce.state },
  });
  return { kind: "authenticated" as const, session };
}
